import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { XIcon, CalendarIcon, SearchIcon, CheckSquareIcon, SquareIcon, LayoutGridIcon, ImageIcon, MonitorIcon, ChevronDownIcon, ChevronRightIcon, MapPinIcon, Building2Icon } from 'lucide-react';
import { Store, MediaType, BookingSlot, StoreTier } from '../../data/types';
import { brands } from '../../data/brands';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
const mediaTypes: MediaType[] = ['Digital Outposts', 'Digital Drive Aisle', 'Digital Windows'];
const mediaTypeIcons: Record<MediaType, React.ReactNode> = {
  'Digital Outposts': <LayoutGridIcon className="w-3.5 h-3.5" data-id="element-390" />,
  'Digital Drive Aisle': <ImageIcon className="w-3.5 h-3.5" data-id="element-391" />,
  'Digital Windows': <MonitorIcon className="w-3.5 h-3.5" data-id="element-392" />
};
const tierOrder: StoreTier[] = ['flagship', 'high-traffic', 'standard'];
const tierLabels: Record<StoreTier, string> = {
  flagship: 'Flagship',
  'high-traffic': 'High Traffic',
  standard: 'Standard'
};
interface SlotKey {
  storeId: string;
  mediaType: MediaType;
}
function slotKeyStr(storeId: string, mediaType: MediaType): string {
  return `${storeId}::${mediaType}`;
}
function parseSlotKey(key: string): SlotKey {
  const [storeId, mediaType] = key.split('::');
  return {
    storeId,
    mediaType: mediaType as MediaType
  };
}
interface GlobalWeekBookingPanelProps {
  isOpen: boolean;
  weekNumber: number;
  currentStoreId: string;
  stores: Store[];
  onClose: () => void;
  onConfirm: (selections: {
    storeId: string;
    mediaType: MediaType;
    slotId: string;
  }[], bookingMode: 'brand' | 'internal', brandName?: string, campaignName?: string) => void;
}
export function GlobalWeekBookingPanel({
  isOpen,
  weekNumber,
  currentStoreId,
  stores: allStores,
  onClose,
  onConfirm
}: GlobalWeekBookingPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [bookingMode, setBookingMode] = useState<'brand' | 'internal'>('internal');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [collapsedTiers, setCollapsedTiers] = useState<Set<StoreTier>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Reset state when panel opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedBrand('');
      setCampaignName('');
      setSelectedKeys(new Set());
      setCollapsedTiers(new Set());
      setIsSubmitting(false);
      setBookingMode('internal');
    }
  }, [isOpen]);
  // Build a lookup: storeId -> mediaType -> BookingSlot for the given week
  const slotLookup = useMemo(() => {
    const lookup = new Map<string, Map<MediaType, BookingSlot>>();
    allStores.forEach(store => {
      const mediaMap = new Map<MediaType, BookingSlot>();
      store.slots.forEach(slot => {
        if (slot.weekNumber === weekNumber) {
          mediaMap.set(slot.mediaType, slot);
        }
      });
      lookup.set(store.id, mediaMap);
    });
    return lookup;
  }, [weekNumber, allStores]);
  // Filter stores by search
  const filteredStores = useMemo(() => {
    if (!searchQuery.trim()) return allStores;
    const q = searchQuery.toLowerCase();
    return allStores.filter(s => s.name.toLowerCase().includes(q) || s.location.toLowerCase().includes(q) || s.province.toLowerCase().includes(q));
  }, [searchQuery, allStores]);
  // Group by tier
  const storesByTier = useMemo(() => {
    const grouped: Record<StoreTier, Store[]> = {
      flagship: [],
      'high-traffic': [],
      standard: []
    };
    filteredStores.forEach(store => {
      grouped[store.tier].push(store);
    });
    return grouped;
  }, [filteredStores]);
  // Get all selectable slot keys (available or blocked)
  const selectableKeys = useMemo(() => {
    const keys = new Set<string>();
    filteredStores.forEach(store => {
      const mediaMap = slotLookup.get(store.id);
      if (!mediaMap) return;
      mediaTypes.forEach(mt => {
        const slot = mediaMap.get(mt);
        if (slot && (slot.status === 'available' || slot.status === 'blocked')) {
          keys.add(slotKeyStr(store.id, mt));
        }
      });
    });
    return keys;
  }, [filteredStores, slotLookup]);
  const toggleSelection = useCallback((key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);
  const selectAll = useCallback(() => {
    setSelectedKeys(new Set(selectableKeys));
  }, [selectableKeys]);
  const deselectAll = useCallback(() => {
    setSelectedKeys(new Set());
  }, []);
  const toggleTier = useCallback((tier: StoreTier) => {
    setCollapsedTiers(prev => {
      const next = new Set(prev);
      if (next.has(tier)) {
        next.delete(tier);
      } else {
        next.add(tier);
      }
      return next;
    });
  }, []);
  const selectAllInTier = useCallback((tier: StoreTier) => {
    const stores = storesByTier[tier];
    setSelectedKeys(prev => {
      const next = new Set(prev);
      stores.forEach(store => {
        const mediaMap = slotLookup.get(store.id);
        if (!mediaMap) return;
        mediaTypes.forEach(mt => {
          const slot = mediaMap.get(mt);
          if (slot && (slot.status === 'available' || slot.status === 'blocked')) {
            next.add(slotKeyStr(store.id, mt));
          }
        });
      });
      return next;
    });
  }, [storesByTier, slotLookup]);
  const formatWeekDates = (wn: number): {
    start: string;
    end: string;
  } => {
    const baseDate = new Date('2026-03-03');
    const startDate = new Date(baseDate);
    startDate.setDate(baseDate.getDate() + (wn - 1) * 7);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6);
    const fmt = (d: Date) => d.toLocaleDateString('en-ZA', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    return {
      start: fmt(startDate),
      end: fmt(endDate)
    };
  };
  const weekDates = formatWeekDates(weekNumber);
  const handleConfirm = async () => {
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 400));
    const selections = Array.from(selectedKeys).map(key => {
      const {
        storeId,
        mediaType
      } = parseSlotKey(key);
      const mediaMap = slotLookup.get(storeId);
      const slot = mediaMap?.get(mediaType);
      return {
        storeId,
        mediaType,
        slotId: slot?.id || ''
      };
    });
    onConfirm(selections, bookingMode, bookingMode === 'brand' ? selectedBrand : undefined, bookingMode === 'internal' ? campaignName : undefined);
    setIsSubmitting(false);
  };
  const isConfirmDisabled = isSubmitting || selectedKeys.size === 0 || bookingMode === 'brand' && !selectedBrand.trim() || bookingMode === 'internal' && !campaignName.trim();
  const allSelected = selectableKeys.size > 0 && Array.from(selectableKeys).every(k => selectedKeys.has(k));
  const getSlotStatus = (storeId: string, mediaType: MediaType) => {
    const mediaMap = slotLookup.get(storeId);
    return mediaMap?.get(mediaType)?.status || 'blocked';
  };
  const getSlotBrand = (storeId: string, mediaType: MediaType) => {
    const mediaMap = slotLookup.get(storeId);
    const slot = mediaMap?.get(mediaType);
    return slot?.brandName || slot?.internalCampaignName;
  };
  const statusLabel = (status: string, brand?: string) => {
    switch (status) {
      case 'available':
        return 'Available';
      case 'booked':
        return brand ? `Booked · ${brand}` : 'Booked';
      case 'blocked':
        return 'Blocked';
      case 'internal':
        return brand ? `Woolworths · ${brand}` : 'Woolworths';
      default:
        return status;
    }
  };
  return <>
      {/* Backdrop */}
      <div className={`
          fixed inset-0 bg-black/30 z-40
          transition-opacity duration-300
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `} onClick={onClose} aria-hidden="true" data-id="element-393" />

      {/* Panel */}
      <aside className={`
          fixed right-0 top-0 h-full w-[720px] max-w-full
          bg-white shadow-2xl z-50
          flex flex-col
          transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `} role="dialog" aria-modal="true" aria-labelledby="global-booking-title" data-id="element-394">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0" data-id="element-395">
          <h2 id="global-booking-title" className="text-lg font-semibold text-text-primary" data-id="element-396">
            Book Week {weekNumber} — All Stores
          </h2>
          <button onClick={onClose} className="p-1.5 text-text-tertiary hover:text-text-primary transition-colors rounded-sm hover:bg-surface" aria-label="Close panel" data-id="element-397">
            <XIcon className="w-5 h-5" data-id="element-398" />
          </button>
        </div>

        {/* Sticky controls */}
        <div className="px-6 py-4 border-b border-border bg-surface-warm flex-shrink-0 space-y-4" data-id="element-399">
          {/* Week info */}
          <div className="flex items-start gap-3 p-3 bg-white rounded-sm border border-border" data-id="element-400">
            <CalendarIcon className="w-5 h-5 text-text-secondary mt-0.5" data-id="element-401" />
            <div data-id="element-402">
              <div className="text-xs text-text-tertiary uppercase tracking-wide mb-0.5" data-id="element-403">
                Booking Period
              </div>
              <div className="text-sm font-medium text-text-primary" data-id="element-404">
                Week {weekNumber}
              </div>
              <div className="text-xs text-text-secondary mt-0.5" data-id="element-405">
                {weekDates.start} — {weekDates.end}
              </div>
            </div>
          </div>

          {/* Search + Select All */}
          <div className="flex items-center gap-3" data-id="element-406">
            <div className="flex-1 relative" data-id="element-407">
              <SearchIcon className="w-4 h-4 text-text-tertiary absolute left-3 top-1/2 -translate-y-1/2" data-id="element-408" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search stores..." className="w-full pl-9 pr-3 py-2 border border-border rounded-sm text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white" data-id="element-409" />
            </div>
            <Button variant="secondary" size="sm" onClick={allSelected ? deselectAll : selectAll} data-id="element-410">
              {allSelected ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          {/* Action mode */}
          <div data-id="element-411">
            <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wide mb-2" data-id="element-412">
              Action
            </label>
            <div className="flex gap-2" data-id="element-413">
              <Button variant={bookingMode === 'brand' ? 'primary' : 'secondary'} size="sm" onClick={() => setBookingMode('brand')} className="flex-1" data-id="element-414">
                Brand Booking
              </Button>
              <Button variant={bookingMode === 'internal' ? 'primary' : 'secondary'} size="sm" onClick={() => setBookingMode('internal')} className="flex-1" data-id="element-415">
                Woolworths Booking
              </Button>
            </div>
          </div>

          {/* Brand selector or campaign name */}
          {bookingMode === 'brand' && <div data-id="element-416">
              <label htmlFor="global-brand-select" className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-417">
                Brand <span className="text-urgent" data-id="element-418">*</span>
              </label>
              <select id="global-brand-select" value={selectedBrand} onChange={e => setSelectedBrand(e.target.value)} className="w-full px-3 py-2 border border-border rounded-sm text-sm text-text-primary bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" data-id="element-419">
                <option value="" data-id="element-420">Select a brand...</option>
                {brands.map(brand => <option key={brand.id} value={brand.name} data-id="element-421">
                    {brand.name}
                  </option>)}
              </select>
            </div>}

          {bookingMode === 'internal' && <div data-id="element-422">
              <label htmlFor="global-campaign-name" className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-423">
                Campaign Name <span className="text-urgent" data-id="element-424">*</span>
              </label>
              <input id="global-campaign-name" type="text" value={campaignName} onChange={e => setCampaignName(e.target.value)} placeholder="e.g. Summer Essentials, Back to School..." className="w-full px-3 py-2 border border-border rounded-sm text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary" data-id="element-425" />
            </div>}
        </div>

        {/* Store list */}
        <div className="flex-1 overflow-y-auto" data-id="element-426">
          {/* Media type column headers */}
          <div className="sticky top-0 z-10 bg-white border-b border-border" data-id="element-427">
            <div className="flex items-center px-6 py-2" data-id="element-428">
              <div className="flex-1 min-w-0" data-id="element-429">
                <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-430">
                  Store
                </span>
              </div>
              <div className="flex items-center gap-1 w-[360px] flex-shrink-0" data-id="element-431">
                {mediaTypes.map(mt => <div key={mt} className="flex-1 flex items-center justify-center gap-1 text-[10px] font-medium text-text-tertiary uppercase tracking-wide" data-id="element-432">
                    <span className="text-text-secondary" data-id="element-433">
                      {mediaTypeIcons[mt]}
                    </span>
                    <span className="hidden lg:inline" data-id="element-434">
                      {mt.replace('Digital ', '')}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          {tierOrder.map(tier => {
          const tierStores = storesByTier[tier];
          if (tierStores.length === 0) return null;
          const isCollapsed = collapsedTiers.has(tier);
          // Count selectable and selected in this tier
          let tierSelectableCount = 0;
          let tierSelectedCount = 0;
          tierStores.forEach(store => {
            mediaTypes.forEach(mt => {
              const key = slotKeyStr(store.id, mt);
              if (selectableKeys.has(key)) {
                tierSelectableCount++;
                if (selectedKeys.has(key)) tierSelectedCount++;
              }
            });
          });
          return <div key={tier} data-id="element-435">
                {/* Tier header */}
                <div className="sticky top-[37px] z-[5] bg-surface-warm border-b border-border" data-id="element-436">
                  <div className="flex items-center justify-between px-6 py-2" data-id="element-437">
                    <button onClick={() => toggleTier(tier)} className="flex items-center gap-2 text-sm font-semibold text-text-primary hover:text-primary transition-colors" data-id="element-438">
                      {isCollapsed ? <ChevronRightIcon className="w-4 h-4" data-id="element-439" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-440" />}
                      <Badge variant={tier} className="text-[10px]" data-id="element-441">
                        {tierLabels[tier]}
                      </Badge>
                      <span className="text-xs text-text-secondary font-normal" data-id="element-442">
                        {tierStores.length} stores
                      </span>
                    </button>
                    <div className="flex items-center gap-3" data-id="element-443">
                      <span className="text-[10px] text-text-tertiary" data-id="element-444">
                        {tierSelectedCount}/{tierSelectableCount} selected
                      </span>
                      {tierSelectableCount > 0 && <button onClick={() => selectAllInTier(tier)} className="text-[10px] font-medium text-primary hover:text-primary/80 transition-colors" data-id="element-445">
                          Select all
                        </button>}
                    </div>
                  </div>
                </div>

                {/* Store rows */}
                {!isCollapsed && tierStores.map((store, idx) => {
              const isCurrentStore = store.id === currentStoreId;
              return <div key={store.id} className={`
                          flex items-center px-6 py-3 border-b border-border
                          ${idx % 2 === 0 ? 'bg-white' : 'bg-surface'}
                          ${isCurrentStore ? 'ring-1 ring-inset ring-primary/20 bg-primary/[0.02]' : ''}
                        `} data-id="element-446">
                        {/* Store info */}
                        <div className="flex-1 min-w-0 pr-4" data-id="element-447">
                          <div className="flex items-center gap-2" data-id="element-448">
                            <span className="text-sm font-medium text-text-primary truncate" data-id="element-449">
                              {store.name}
                            </span>
                            {isCurrentStore && <span className="text-[9px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-1.5 py-0.5 rounded-sm flex-shrink-0" data-id="element-450">
                                Current
                              </span>}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5" data-id="element-451">
                            <span className="text-xs text-text-tertiary flex items-center gap-1" data-id="element-452">
                              <MapPinIcon className="w-3 h-3" data-id="element-453" />
                              {store.location}
                            </span>
                            <span className="text-xs text-text-tertiary" data-id="element-454">
                              {store.province}
                            </span>
                          </div>
                        </div>

                        {/* Media type checkboxes */}
                        <div className="flex items-center gap-1 w-[360px] flex-shrink-0" data-id="element-455">
                          {mediaTypes.map(mt => {
                    const status = getSlotStatus(store.id, mt);
                    const brand = getSlotBrand(store.id, mt);
                    const key = slotKeyStr(store.id, mt);
                    const isSelectable = status === 'available' || status === 'blocked';
                    const isSelected = selectedKeys.has(key);
                    return <button key={mt} onClick={() => isSelectable && toggleSelection(key)} disabled={!isSelectable} className={`
                                  flex-1 flex flex-col items-center justify-center gap-0.5 py-2 px-1 rounded-sm border transition-colors text-center min-h-[52px]
                                  ${!isSelectable ? status === 'booked' ? 'bg-booked/10 border-booked/30 cursor-default' : status === 'internal' ? 'bg-internal-bg border-internal/20 cursor-default' : 'bg-surface border-border cursor-default' : isSelected ? 'bg-available-bg border-available/40 cursor-pointer' : 'bg-white border-border hover:border-text-tertiary cursor-pointer'}
                                `} aria-label={`${mt} at ${store.name}: ${statusLabel(status, brand)}`} data-id="element-456">
                                {isSelectable ? isSelected ? <CheckSquareIcon className="w-4 h-4 text-available" data-id="element-457" /> : <SquareIcon className="w-4 h-4 text-text-tertiary" data-id="element-458" /> : <div className={`w-2 h-2 rounded-full ${status === 'booked' ? 'bg-booked' : status === 'internal' ? 'bg-internal' : 'bg-blocked'}`} data-id="element-459" />}
                                <span className={`text-[9px] leading-tight truncate max-w-full px-0.5 ${isSelectable && isSelected ? 'text-available font-medium' : isSelectable ? 'text-text-tertiary' : status === 'internal' ? 'text-internal' : 'text-text-tertiary'}`} data-id="element-460">
                                  {isSelectable ? isSelected ? 'Selected' : status === 'available' ? 'Available' : 'Blocked' : status === 'booked' ? brand ? brand.length > 12 ? brand.slice(0, 11) + '…' : brand : 'Booked' : status === 'internal' ? 'Woolworths' : 'Unavailable'}
                                </span>
                              </button>;
                  })}
                        </div>
                      </div>;
            })}
              </div>;
        })}

          {filteredStores.length === 0 && <div className="px-6 py-12 text-center text-text-tertiary text-sm" data-id="element-461">
              No stores match your search.
            </div>}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border bg-surface flex-shrink-0 space-y-2" data-id="element-462">
          <Button variant="primary" size="lg" onClick={handleConfirm} disabled={isConfirmDisabled} className="w-full" data-id="element-463">
            {isSubmitting ? 'Processing...' : bookingMode === 'internal' ? `Book ${selectedKeys.size} Slot${selectedKeys.size !== 1 ? 's' : ''} — Woolworths` : `Book ${selectedKeys.size} Slot${selectedKeys.size !== 1 ? 's' : ''} for Brand`}
          </Button>
          <Button variant="ghost" size="lg" onClick={onClose} className="w-full" data-id="element-464">
            Cancel
          </Button>
        </div>
      </aside>
    </>;
}